import { useCallback, useEffect, useState } from 'react';

import { useNewUnsavedData } from '@app/hooks';
import type { ConfirmResult } from '@app/hooks/useNewUnsavedData';
import type { NEmployees } from '@app/types/Employees';

type UseEditEmployeeUnsavedChangesArgs = {
  isUpdated: boolean;
  setIsUpdated: (value: boolean) => void;
  submitCurrentForm: () => Promise<boolean>;
};

type UseRegisterUnsavedChangesArgs = {
  handleUnsavedChanges: NEmployees.HandleUnsavedEmployeeInfoTab;
  setHandleUnsavedChanges: (
    handler: NEmployees.HandleUnsavedEmployeeInfoTab | null
  ) => void;
};

export const useEditEmployeeUnsavedChanges = ({
  isUpdated,
  setIsUpdated,
  submitCurrentForm,
}: UseEditEmployeeUnsavedChangesArgs) => {
  const [shouldConfirmUnsaved, setShouldConfirmUnsaved] = useState(false);
  const [resolveUnsavedConfirm, setResolveUnsavedConfirm] = useState<
    ((action: ConfirmResult) => void) | null
  >(null);

  const { confirm: confirmUnsavedData } = useNewUnsavedData({
    hasChanges: shouldConfirmUnsaved,
    title: 'Несохранённые изменения',
    message:
      'Вы действительно хотите покинуть страницу, не сохранив изменения?',
    confirmText: 'Сохранить',
    cancelText: 'Отмена',
    discardText: 'Уйти без изменений',
    type: 'warning',
    dataTestId: 'unsaved-changes-popup',
  });

  useEffect(() => {
    if (!shouldConfirmUnsaved || !resolveUnsavedConfirm) {
      return;
    }

    const openConfirm = async () => {
      const action = await confirmUnsavedData();

      resolveUnsavedConfirm(action);
      setResolveUnsavedConfirm(null);
      setShouldConfirmUnsaved(false);
    };

    openConfirm();
  }, [confirmUnsavedData, resolveUnsavedConfirm, shouldConfirmUnsaved]);

  const requestUnsavedConfirmation = useCallback(() => {
    return new Promise<ConfirmResult>((resolve) => {
      setResolveUnsavedConfirm(() => resolve);
      setShouldConfirmUnsaved(true);
    });
  }, []);

  const handleUnsavedChanges = useCallback(async () => {
    if (!isUpdated) {
      return true;
    }

    const action = await requestUnsavedConfirmation();

    switch (action) {
      case 'confirm':
        return submitCurrentForm();
      case 'discard':
        setIsUpdated(false);
        return true;
      case 'cancel':
      default:
        return false;
    }
  }, [isUpdated, requestUnsavedConfirmation, setIsUpdated, submitCurrentForm]);

  return { handleUnsavedChanges };
};

export const useRegisterEditEmployeeUnsavedChanges = ({
  handleUnsavedChanges,
  setHandleUnsavedChanges,
}: UseRegisterUnsavedChangesArgs) => {
  useEffect(() => {
    setHandleUnsavedChanges(handleUnsavedChanges);

    return () => {
      setHandleUnsavedChanges(null);
    };
  }, [handleUnsavedChanges, setHandleUnsavedChanges]);
};
